import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { IEventCore } from '../../models/IEvent';
import { Typography, Container, Grid, Theme, Fab, Box, Tooltip } from '@mui/material';
import AuthContext from '../../services/Auth/AuthContext';

import { useNavigate, useParams } from 'react-router-dom';
import LiveEvent from './LiveEvent';
import RidersTable from './RidersTable';
import IRider from '../../models/IRider';
import { SnackbarProvider } from 'notistack';
import EventServiceInstance from '../../services/EventService';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import EventHeader from './EventHeader';
import { useTranslation } from 'react-i18next';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            paddingTop: theme.spacing(10),
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
        fab: {
            position: 'fixed',
            bottom: theme.spacing(2),
            right: theme.spacing(2),
        },
        fabUnmask: {
            position: 'fixed',
            bottom: theme.spacing(2),
            right: theme.spacing(10),
        },
    })
);

interface IEventToolbarProps {
    eventId: string;
}
function EventToolbar(props: IEventToolbarProps) {
    const navigate = useNavigate();
    return (
        <Fab
            onClick={() => navigate(`/event/setup/${props.eventId}`)}
            color="primary"
            aria-label="edit"
            className={useStyles().fab}
        >
            <EditIcon />
        </Fab>
    );
}

interface IUnmaskButtonProps {
    unmasked: boolean;
    onToggle: () => void;
}
function UnmaskButton(props: IUnmaskButtonProps) {
    const classes = useStyles();
    const { t } = useTranslation('common');
    return (
        <Tooltip title={props.unmasked ? t('events.maskResults') : t('events.unmaskResults')}>
            <Fab
                onClick={props.onToggle}
                color={props.unmasked ? 'secondary' : 'default'}
                aria-label={props.unmasked ? 'mask results' : 'unmask results'}
                className={classes.fabUnmask}
            >
                {props.unmasked ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </Fab>
        </Tooltip>
    );
}

function EventDetail() {
    const { eventId } = useParams();
    const [eventData, setEventData] = useState<IEventCore | null>();
    const [riders, setRiders] = useState<IRider[]>([]);
    const [unmasked, setUnmasked] = useState(false);
    const authService = useContext(AuthContext);

    useEffect(() => {
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            if (!eventId) {
                throw new Error("EventId is not defined");
            }
            const evt = await EventServiceInstance.getEvent(eventId, source.token);
            if (evt?.status === 1 || evt?.status === 2) {
                const riders = await EventServiceInstance.getRiders(eventId, unmasked, source.token);
                setEventData(evt);
                setRiders(riders);
            } else {
                setEventData(evt);
            }
        };
        fetchData().catch((error) => {
            if (!axios.isCancel(error)) {
                console.error(error);
            }
        });
        return () => {
            source.cancel();
        };
    }, [eventId, unmasked]);

    const classes = useStyles();
    const { t } = useTranslation('common');

    if (!eventId) {
        throw new Error("EventId is not defined");
    }

    const isOrganizer = authService?.getIsOrganizer(eventData?.organizerId ?? '');
    const eventsNameMap: Record<string, string> = { [t('mainList.events')]: '/events' };

    return (
        <Container maxWidth={false}>
            <Box className={classes.container}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={eventsNameMap} current={eventData?.name ?? ''} />
                    </Grid>
                </Grid>
                {eventData?.status === 1 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <SnackbarProvider maxSnack={3}>
                                {isOrganizer && <EventToolbar eventId={eventId} />}
                                {isOrganizer && eventData?.raceMode === false && (
                                    <UnmaskButton
                                        unmasked={unmasked}
                                        onToggle={() => setUnmasked(prev => !prev)}
                                    />
                                )}
                                <EventHeader
                                    title={eventData?.name}
                                    date={eventData?.eventDate}
                                    organizerName={eventData?.organizerName}
                                />
                                <LiveEvent eventId={eventId} isOrganizer={isOrganizer} groups={eventData?.groups} unmasked={unmasked} riders={riders} />
                            </SnackbarProvider>
                        </Grid>
                    </Grid>
                )}
                {eventData?.status === 2 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {isOrganizer && <EventToolbar eventId={eventId} />}
                            {isOrganizer && eventData?.raceMode === false && (
                                <UnmaskButton
                                    unmasked={unmasked}
                                    onToggle={() => setUnmasked(prev => !prev)}
                                />
                            )}
                            <EventHeader
                                title={eventData?.name}
                                date={eventData?.eventDate}
                                organizerName={eventData?.organizerName}
                            />
                            <RidersTable eventId={eventId} riders={riders} isOrganizer={isOrganizer} groups={eventData?.groups} />
                        </Grid>
                    </Grid>
                )}
                {eventData?.status === 0 && (
                    <Typography variant="body1" align="center">
                        Event has not started yet.
                    </Typography>
                )}
            </Box>
        </Container>
    );
}

export default EventDetail;